import React, { useContext, useState } from 'react';
import parse from 'html-react-parser';
import { Alert, Box, Button, Card, CardContent, CardHeader, IconButton, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import useAssignmentMetrics from '../_data_sources/useAssignmentMetrics';
import usePendingAssignmentsSummary from '../_data_sources/usePendingAssignmentSummary';
import useOverdueWarnings from '../_data_sources/useOverdueWarnings';
import { error, neutral, success, warning } from '_lex/_foundations/phxColors';
import GroupPicker from '../../_components/GroupPicker';
import Loading from 'views/App/Portal/Loading';
import PendingAssignmentsSummaryDisplay from './PendingAssignmentsSummary';
import { ChevronRight, MailOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

type AssignmentMetricStyles = {
  overdue: any;
  dueSoon: any;
  completed: any;
};

export default function Assignments() {
  const navigate = useNavigate();
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const [group, setGroup] = useState<string | null>(null);
  const metricsQuery = useAssignmentMetrics(currentOrganizationId, { group });
  const pendingAssignmentsQuery = usePendingAssignmentsSummary(currentOrganizationId, { group });
  const overdueWarningsQuery = useOverdueWarnings(currentOrganizationId, { group });
  const assignmentMetricStyles: AssignmentMetricStyles = {
    overdue: {
      backgroundColor: error[25],
      borderColor: error[600],
    },
    dueSoon: {
      backgroundColor: warning[25],
      borderColor: warning[600],
    },
    completed: {
      backgroundColor: success[25],
      borderColor: success[600],
    },
  };

  const handleNavigationToViewAllForUser = (id: string) => {
    navigate(`/admin/reporting/policy-acknowledgments#users=%5B"${id}"%5D&status=OVERDUE`);
  };
  return (
    <Card className='assignment-metrics lex-dashboard-card'>
      <CardHeader
        title={t('dashboard.policy.assignments.title')}
        action={
          <>
            {hasPermission('listGroups', currentOrganizationId) && (
              <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupPicker
                  id='groups_filter'
                  size='small'
                  placeholder={t('dashboard.policy.assignments.groupFilter.placeholder')}
                  organizationId={currentOrganizationId}
                  value={group ? [group] : undefined}
                  onChange={(value) => setGroup(value[0])}
                  allowBlank
                />
              </Box>
            )}
          </>
        }
      />
      <CardContent>
        <Stack spacing={8}>
          <Stack spacing={4}>
            <Box sx={{ display: 'flex', gap: 12, alignItems: 'end', justifyContent: 'space-between' }}>
              <Box
                sx={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 3,
                }}
              >
                {Object.entries(metricsQuery.data ?? {}).map(([key, value], index) => (
                  <Box key={`metric_box_${index}`}>
                    {key !== 'completed' && (
                      <Box
                        key={`metric_${key}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          px: 3,
                          py: 2,
                          borderRadius: '4px 16px 4px 4px',
                          border: `solid 1px ${neutral[200]}`,
                          ...(assignmentMetricStyles[key as keyof AssignmentMetricStyles] ?? {}),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 24,
                            fontWeight: 500,
                            color: neutral[800],
                          }}
                        >
                          {value}
                        </Typography>
                        <Typography
                          variant='body1'
                          sx={{
                            fontSize: 18,
                            color: neutral[600],
                            fontWeight: 400,
                          }}
                        >
                          {parse(t(`dashboard.policy.assignments.metrics.${key}`))}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              <Button
                color='secondary'
                size='small'
                endIcon={<ChevronRight />}
                href='/admin/reporting/policy-acknowledgments#completed=false'
              >
                {t('dashboard.policy.assignments.viewAll')}
              </Button>
            </Box>
            <Box>
              {pendingAssignmentsQuery.isLoading ? (
                <Loading />
              ) : (
                <>
                  {!pendingAssignmentsQuery.data || pendingAssignmentsQuery.data.length === 0 ? (
                    <Alert severity='info'>{t('dashboard.policy.assignments.empty')}</Alert>
                  ) : (
                    <Stack
                      sx={{
                        backgroundColor: neutral[25],
                        border: `solid 1px ${neutral[200]}`,
                        borderRadius: 2,
                        '& .pending-assignments-summary': {
                          borderBottom: `1px solid ${neutral[300]}`,
                          '&:first-of-type': {
                            borderRadius: '8px 8px 0 0',
                          },
                          '&:last-of-type': {
                            borderBottom: 0,
                            borderRadius: '0 0 8px 8px',
                          },
                        },
                      }}
                    >
                      {pendingAssignmentsQuery.data.map((summary, ind) => (
                        <PendingAssignmentsSummaryDisplay
                          key={`summary_${ind}`}
                          summary={summary}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Box>
            <Box>
              {overdueWarningsQuery.isLoading ? (
                <Loading />
              ) : (
                <Card className='assignment-metrics lex-dashboard-card'>
                  <CardHeader
                    title={t('dashboard.policy.assignments.overdueWarnings.title')}
                    action={
                      <>
                        <Button
                          color='secondary'
                          size='small'
                          endIcon={<ChevronRight />}
                          href='/admin/reporting/policy-acknowledgments?#status=OVERDUE'
                        >
                          {t('dashboard.policy.assignments.overdueWarnings.viewAll')}
                        </Button>
                      </>
                    }
                  />
                  <CardContent>
                    {!overdueWarningsQuery.data || overdueWarningsQuery.data.length === 0 ? (
                      <Alert severity='info'>{t('dashboard.policy.assignments.overdueWarnings.empty')}</Alert>
                    ) : (
                      <Card>
                        <CardContent
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 3,
                          }}
                        >
                          {overdueWarningsQuery.data.map((item) => (
                            <Box
                              key={`overdue_warning_${item.id}`}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                px: 4,
                                py: 3,
                                border: `solid 1px ${neutral[200]}`,
                                borderRadius: 2,
                                backgroundColor: neutral[50],
                              }}
                            >
                              <Stack>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    onClick={() => handleNavigationToViewAllForUser(item.id)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {[item.firstName, item.lastName].join(' ').trim()}
                                  </Typography>
                                  <IconButton href={`mailto:${item.email}`}>
                                    <MailOutlined />
                                  </IconButton>
                                </Box>
                                <Typography
                                  sx={{ color: error[800], cursor: 'pointer' }}
                                  onClick={() => handleNavigationToViewAllForUser(item.id)}
                                >
                                  {item.overdueAssignments === 1
                                    ? t('dashboard.policy.assignments.overdueWarnings.overdueAssignmentsSingular', '', {
                                        count: item.overdueAssignments.toString(),
                                      })
                                    : t('dashboard.policy.assignments.overdueWarnings.overdueAssignments', '', {
                                        count: item.overdueAssignments.toString(),
                                      })}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
